// frontend/src/pages/employees/EntriesDrawer.jsx
import React, { useEffect } from "react";
import { hours as fmtHours } from "./format";
import { useAnalytics } from "./useAnalytics";

const PAGE = 100;

// zIndex 1000/1001 matches components/TimelineDrawer.jsx (lines 92 and
// 101), the app's other slide-over drawer -- that drawer works on three
// pages today with this exact pair. TopBar (components/TopBar.jsx:152) is
// position:fixed with zIndex 1000 and is rendered before routed content
// (App.js:132). With an EQUAL z-index, CSS resolves the tie by DOM order
// in the LATER element's favour -- this drawer, not TopBar -- so the
// backdrop's zIndex:1000 already matches TopBar's exactly on that
// tiebreak alone and does not need to be higher. Kept explicit anyway
// (matching TimelineDrawer's precedent, which works on three pages today)
// rather than relying on an implicit DOM-order rule holding forever. The
// sheet's zIndex:1001 is inert as written -- the backdrop is already
// position:fixed with its own z-index, which opens a new stacking context
// that the sheet's 1001 cannot escape -- kept as belt-and-braces in case
// that nesting ever changes, not because it does anything today.
const backdrop = {
  position: "fixed", inset: 0, background: "rgba(17,24,39,0.35)",
  display: "flex", justifyContent: "flex-end", zIndex: 1000,
};
const sheet = {
  background: "white", width: "min(680px, 100%)", height: "100%",
  overflowY: "auto", padding: 24, boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
  zIndex: 1001,
};
const th = { padding: "8px 6px", fontWeight: 600, textAlign: "right",
             borderBottom: "2px solid #e5e7eb", fontSize: 12,
             textTransform: "uppercase", color: "#6b7280" };
const thLeft = { ...th, textAlign: "left" };
const td = { padding: "8px 6px", textAlign: "right",
             borderBottom: "1px solid #f3f4f6" };
const tdLeft = { ...td, textAlign: "left" };

/**
 * The rows behind one number.
 *
 * Read-only. Its footer total must equal the figure on the row or bar
 * that opened it, and when it does not it SAYS SO -- a drawer quietly
 * showing a different number than the row above it is worse than no
 * drawer at all in an accounting application.
 *
 * The "open in Time Entry" link carries no parameters on purpose:
 * TimeEntryPage reads none (it has no useSearchParams at all), so a
 * filtered-looking link would be a lie. Spec section 11 holds that fix
 * as its own ticket.
 */
export default function EntriesDrawer({
  open, title, params, expectedHours, onClose,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const { data, loading, error } = useAnalytics(
    open ? "entries/" : null, { ...params, limit: PAGE });

  if (!open) return null;

  const rows = (data && data.results) || [];
  const count = data ? data.count : 0;
  const partial = count > rows.length;
  const total = rows.reduce((sum, r) => sum + Number(r.hours || 0), 0);

  // Only a COMPLETE page can disagree with the row that opened it. A
  // partial page legitimately sums to less, and flagging that would
  // train people to ignore the warning.
  const mismatch =
    !loading && !error && !partial && expectedHours != null &&
    Math.abs(total - Number(expectedHours)) > 0.005;

  return (
    <div style={backdrop} onClick={onClose}>
      <div
        style={sheet}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{ background: "none", border: "none", fontSize: 20,
                     cursor: "pointer", color: "#6b7280", lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {loading && <div style={{ color: "#6b7280" }}>Loading…</div>}
        {error && <div role="alert" style={{ color: "#b91c1c" }}>{error}</div>}

        {mismatch && (
          <div role="alert" style={{
            background: "#fffbeb", border: "1px solid #fde68a",
            borderRadius: 8, padding: "10px 12px", marginBottom: 16,
            fontSize: 13, color: "#92400e",
          }}>
            These entries total {fmtHours(total)} hours, but the row that
            opened this said {fmtHours(expectedHours)}. One of the two is
            wrong — do not rely on either until it is explained.
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div style={{ color: "#6b7280" }}>No entries behind this figure.</div>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse",
                            fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={thLeft}>Date</th>
                  <th style={thLeft}>Client</th>
                  <th style={thLeft}>Code</th>
                  <th style={th}>Hrs</th>
                  <th style={thLeft}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={tdLeft}>{r.date}</td>
                    <td style={tdLeft}>{r.client_name}</td>
                    <td style={tdLeft}>{r.time_code}</td>
                    <td style={td}>{fmtHours(r.hours)}</td>
                    <td style={tdLeft}>{r.notes}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td style={{ ...tdLeft, borderTop: "2px solid #e5e7eb" }}
                      colSpan={3}>
                    Total
                  </td>
                  <td data-testid="drawer-total"
                      style={{ ...td, borderTop: "2px solid #e5e7eb" }}>
                    {fmtHours(total)}
                  </td>
                  <td style={{ ...td, borderTop: "2px solid #e5e7eb" }} />
                </tr>
              </tfoot>
            </table>

            {partial && (
              <div style={{ marginTop: 12, fontSize: 12, color: "#6b7280" }}>
                Showing {rows.length} of {count} entries.
              </div>
            )}
          </>
        )}

        <div style={{ marginTop: 20 }}>
          <a href="/time" target="_blank" rel="noopener noreferrer"
             style={{ color: "#3b82f6", fontSize: 13, fontWeight: 600 }}>
            ▸ Open Time Entry to edit
          </a>
        </div>
      </div>
    </div>
  );
}
